import {Box, Tabs, Tab} from "@mui/material";
import * as React from 'react';
import TabPanel from "../../../../share/components/tab/TabPanel";
import { Shipment, toString } from "../../../types/models/Shipment";
import LogListItem from "../../../../timber/components/log/LogList/LogListItem";
import CordwoodListItem from "../../../../timber/components/cordwood/CordwoodList/CordwoodListItem";
import ShipmentHistoryListItem from "../../../../timber/components/shipmentHistory/ShipmentHistoryList/ShipmentHistoryListItem";
import {useStateSession} from "../../../../hooks/useAuth";
import { t } from "@lingui/core/macro";
import {ShipmentType} from "../../../types/models/ShipmentType.d";
import LogShipmentUpload from "../ShipmentRegulator/LogShipmentUpload.tsx";
import GenericRootReport, {defaultFormOptions} from "../../generic/report/GenericRootReport.tsx";
import LogShipmentReportPdfDialog from "../ShipmentRegulator/LogShipmentReportPdfDialog.tsx";
import CordwoodShipmentReportPdfDialog from "../ShipmentRegulator/CordwoodShipmentReportPdfDialog.tsx";
import LogListItemForShipment from "../ShipmentRegulator/LogListItemForShipment.tsx";
import CordwoodListItemForShipment from "../ShipmentRegulator/CordwoodListItemForShipment.tsx";
import PriceCalculator from "../../generic/priceCalculator/PriceCalculator.tsx";
import {
    cuttingStateEditCheck,
    shippingStateCancelCheck,
    shippingStateCheck,
    shippingStateEditCheck
} from "../../../stateChecks.ts";
import {useGetCuttingQuery} from "../../../apis/Cutting.api.ts";
import {PrimaryCutting} from "../../../types/models/Cutting.ts";
import LogListItemFromHistory from "../ShipmentRegulator/LogListItemFromHistory.tsx";
import CordwoodListItemForHistory from "../ShipmentRegulator/CordwoodListItemFromHistory.tsx";


type IFormProps = {
    data: Shipment;
};

const ShipmentTabs: React.FC<IFormProps> = ({ data = {} }) => {

    const session = useStateSession()
    const searchParams = new URLSearchParams(window.location.search)

    const [tabValue, setTabValue] = React.useState(searchParams.get('tab') || (data.shipmentType === ShipmentType.Cordwood ? 'cordwood' : 'log'));
    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        
        if (searchParams.has('tab')) {
            searchParams.set('tab', newValue)
        } else {
            searchParams.append('tab', newValue)
        }
        window.history.pushState({}, '', '?' + searchParams.toString())

        setTabValue(newValue);
    };

    const customizeFiler = (filter: Record<string, string>): Record<string, string> =>  { return {
        ...filter,
        shipmentId: data.id,
        shipmentType: data.shipmentType,
    }}
    const reportTitle = `Shipment ${toString(data)}`
    const formOptions = {
        ...defaultFormOptions,
        showShipmentType: false,
        showCutting: false,
        showStorage: false,
        showShipmentDate: false,
    }

    const { data: cutting } = useGetCuttingQuery(PrimaryCutting.fromId(data?.cuttingId), {skip: !data?.cuttingId})

    const stateAllowed = shippingStateCheck(session, data?.state)
    const shippingStateEdit = shippingStateEditCheck(data?.state)
    const shippingStateCancel = shippingStateCancelCheck(data?.state)
    const cuttingStateEdit = cuttingStateEditCheck(cutting?.state)

    if (data.shipmentType === ShipmentType.Cordwood)
    return (
        <Box sx={ { width: '100%' } }>
            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                data-testid="Shipment-tabs"
            >
                { session.hasPermission('', 'applications/timber/entities/Cordwoods/list/view') &&
                    <Tab label={t`Cordwoods`} value="cordwood"/>
                }
                { session.hasPermission('', 'applications/timber/entities/Shipments/report/view') &&
                    !shippingStateCancel &&
                    <Tab label={t`Report`} value="report"/>
                }
                { session.hasPermission('', 'applications/timber/entities/PricingProfiles/view') &&
                    !shippingStateCancel &&
                    <Tab label={t`Price Calculator`} value="priceCalculatorReport"/>
                }
                { session.hasPermission('', 'applications/timber/entities/ShipmentHistories/list/view') &&
                    <Tab label={t`Shipment Histories`} value="shipmentHistory"/>
                }
                </Tabs>

            { session.hasPermission('', 'applications/timber/entities/Cordwoods/list/view') &&
                <TabPanel value={tabValue} index="cordwood">
                    { shippingStateCancel ?
                        <CordwoodListItemForHistory shipment={data}/>
                        :
                        <CordwoodListItemForShipment shipment={data}/>
                    }
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/Shipments/report/view') &&
                !shippingStateCancel &&
                <TabPanel value={tabValue} index="report">
                    <GenericRootReport
                        shipmentDefined={true}
                        showShipments={false}
                        mainTitle={reportTitle}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                        toolbar={(groupedData) =>
                            <CordwoodShipmentReportPdfDialog groupedData={groupedData} shipment={data}/>
                        }
                    />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/PricingProfiles/view') &&
                !shippingStateCancel &&
                <TabPanel value={tabValue} index="priceCalculatorReport">
                    <PriceCalculator
                        shipmentDefined={null}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                    />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/ShipmentHistories/list/view') &&
                <TabPanel value={tabValue} index="shipmentHistory" data-testid="ShipmentHistory-tab">
                    <ShipmentHistoryListItem shipmentId={data?.id} showAdd={ true } />
                </TabPanel>
            }
        </Box>
    )
    else
        return (
            <Box sx={ { width: '100%' } }>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="scrollable auto tabs"
                >
                { session.hasPermission('', 'applications/timber/entities/Logs/list/view') &&
                    <Tab label={t`Logs`} value="log"/>
                }
                { session.hasPermission('', 'applications/timber/entities/Shipments/report/view') &&
                    !shippingStateCancel &&
                    <Tab label={t`Report`} value="report"/>
                }
                { session.hasPermission('', 'applications/timber/entities/PricingProfiles/view') &&
                    !shippingStateCancel &&
                    <Tab label={t`Price Calculator`} value="priceCalculatorReport"/>
                }
                { session.hasPermission('', 'applications/timber/entities/Shipments/logUpload/view') &&
                    cuttingStateEdit &&
                    shippingStateEdit &&
                    stateAllowed &&
                    <Tab label={t`Upload`} value="upload"/>
                }
                { session.hasPermission('', 'applications/timber/entities/ShipmentHistories/list/view') &&
                    <Tab label={t`Shipment Histories`} value="shipmentHistory"/>
                }
                </Tabs>

            { session.hasPermission('', 'applications/timber/entities/Logs/list/view') &&
                <TabPanel value={tabValue} index="log">
                    { shippingStateCancel ?
                        <LogListItemFromHistory shipment={data}/>
                        :
                        <LogListItemForShipment shipment={data}/>
                    }
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/Shipments/report/view') &&
                !shippingStateCancel &&
                <TabPanel value={tabValue} index="report">
                    <GenericRootReport
                        shipmentDefined={true}
                        showShipments={false}
                        mainTitle={reportTitle}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                        toolbar={(groupedData) =>
                            <LogShipmentReportPdfDialog groupedData={groupedData} shipment={data}/>
                        }
                    />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/PricingProfiles/view') &&
                !shippingStateCancel &&
                <TabPanel value={tabValue} index="priceCalculatorReport">
                    <PriceCalculator
                        shipmentDefined={null}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                    />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/Shipments/logUpload/view') &&
                cuttingStateEdit &&
                shippingStateEdit &&
                stateAllowed &&
                <TabPanel value={tabValue} index="upload">
                    <LogShipmentUpload id={data.id}/>
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/ShipmentHistories/list/view') &&
                <TabPanel value={tabValue} index="shipmentHistory" data-testid="ShipmentHistory-tab">
                    <ShipmentHistoryListItem shipmentId={data?.id} showAdd={ true } />
                </TabPanel>
            }
        </Box>
    )
}

export default ShipmentTabs;
